import * as React from "react";
import { cn } from "../lib/utils";

export interface DashboardTileAction {
  label: string;
  /** Optional icon for the action (Lucide component). */
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

export interface DashboardTileProps {
  /** Tile title — usually module name or category. */
  title: string;
  /** Tile icon (Lucide component). */
  icon: React.ComponentType<{ className?: string }>;
  /** Primary count or KPI value (large, bold). */
  primaryValue?: string | number;
  /** Label for the primary value (e.g., "open work orders"). */
  primaryLabel?: string;
  /** Optional secondary stat for context (e.g., "+3 this week"). */
  secondaryValue?: string;
  /** Tone for the icon background — green (good), amber (warn), red (alert), blue (info), default. */
  tone?: "default" | "info" | "good" | "warn" | "alert";
  /** Optional action buttons rendered as a row at the bottom. */
  actions?: DashboardTileAction[];
  /** If set, the whole tile is clickable. */
  onClick?: () => void;
  className?: string;
}

/**
 * Odoo-style dashboard tile — icon-left, title above value, optional actions.
 * Info-dense layout: icon and primary value share a row so the tile stays
 * compact while the title floats above the value for scannability.
 */
export function DashboardTile({
  title,
  icon: Icon,
  primaryValue,
  primaryLabel,
  secondaryValue,
  tone = "default",
  actions,
  onClick,
  className,
}: DashboardTileProps) {
  const toneStyles: Record<NonNullable<DashboardTileProps["tone"]>, string> = {
    default: "bg-muted text-muted-foreground",
    info: "bg-brand-primary/10 text-brand-primary",
    good: "bg-[hsl(var(--state-running))]/10 text-[hsl(var(--state-running))]",
    warn: "bg-[hsl(var(--alarm-minor))]/10 text-[hsl(var(--alarm-minor))]",
    alert: "bg-[hsl(var(--alarm-critical))]/10 text-[hsl(var(--alarm-critical))]",
  };

  return (
    <div
      className={cn(
        "group flex flex-col rounded-lg border bg-card p-4 shadow-sm transition-all",
        onClick &&
          "cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className,
      )}
      // Button semantics + keyboard activation only when the tile is actually
      // clickable; a static tile stays a plain container.
      {...(onClick
        ? {
            role: "button",
            tabIndex: 0,
            onClick,
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-md transition-colors",
            toneStyles[tone],
          )}
        >
          <Icon className="h-6 w-6" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading text-sm font-semibold text-foreground truncate">{title}</h3>
            {secondaryValue && (
              <span className="shrink-0 text-xs font-medium text-muted-foreground">{secondaryValue}</span>
            )}
          </div>
          {primaryValue !== undefined && (
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span className="font-heading text-2xl font-bold leading-tight tracking-tight">{primaryValue}</span>
              {primaryLabel && (
                <span className="text-xs text-muted-foreground line-clamp-1">{primaryLabel}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {actions && actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1 border-t pt-2">
          {actions.map((action) => (
            <button
              key={action.label}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {action.icon ? <action.icon className="h-3.5 w-3.5" /> : null}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Per-breakpoint column count. 1–6 columns are supported at each breakpoint;
 * the base (mobile) count is always 1.
 */
export interface DashboardTileColumns {
  /** Columns from the `sm` breakpoint up. Default 2. */
  sm?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Columns from the `lg` breakpoint up. Default 3. */
  lg?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Columns from the `xl` breakpoint up. Default 4. */
  xl?: 1 | 2 | 3 | 4 | 5 | 6;
}

// Static class maps so the column utilities are literal strings Tailwind's JIT
// scanner can see (a templated `sm:grid-cols-${n}` would be purged).
const SM_COLS: Record<number, string> = {
  1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3",
  4: "sm:grid-cols-4", 5: "sm:grid-cols-5", 6: "sm:grid-cols-6",
};
const LG_COLS: Record<number, string> = {
  1: "lg:grid-cols-1", 2: "lg:grid-cols-2", 3: "lg:grid-cols-3",
  4: "lg:grid-cols-4", 5: "lg:grid-cols-5", 6: "lg:grid-cols-6",
};
const XL_COLS: Record<number, string> = {
  1: "xl:grid-cols-1", 2: "xl:grid-cols-2", 3: "xl:grid-cols-3",
  4: "xl:grid-cols-4", 5: "xl:grid-cols-5", 6: "xl:grid-cols-6",
};

/**
 * A grid wrapper that tiles dashboard tiles responsively.
 * Defaults: 1 column on mobile, 2 on tablet (sm), 3 on desktop (lg), 4 on wide
 * (xl). Override any breakpoint via `columns={{ sm, lg, xl }}` — unspecified
 * breakpoints keep their default.
 */
export interface DashboardTileGridProps {
  children: React.ReactNode;
  className?: string;
  /** Per-breakpoint column counts. Omitted breakpoints fall back to defaults. */
  columns?: DashboardTileColumns;
}

export function DashboardTileGrid({ children, className, columns }: DashboardTileGridProps) {
  const sm = columns?.sm ?? 2;
  const lg = columns?.lg ?? 3;
  const xl = columns?.xl ?? 4;
  return (
    <div
      className={cn(
        "grid gap-3",
        SM_COLS[sm],
        LG_COLS[lg],
        XL_COLS[xl],
        className,
      )}
    >
      {children}
    </div>
  );
}
